import { type ComponentType, type ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { AlertTriangle, Lightbulb } from "lucide-react";

import { getAiReputationReport } from "../../api/aiReputation";
import type {
  AiReputationClaim,
  AiReputationReport,
  AiReputationSource,
} from "../../types/aiReputation";
import {
  groupSourcesByDomain,
  humanizeAiReputationValue,
  isSafeOutboundUrl,
} from "../../utils/aiReputation";

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = ["Overview", "Sources", "What to do", "Alignment"] as const;
type Tab = (typeof TABS)[number];

const CATEGORY_META: Record<string, { label: string; bg: string; text: string; bar: string }> = {
  own_site: {
    label: "Your website",
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    bar: "bg-emerald-500",
  },
  ota: { label: "OTA listings", bg: "bg-blue-100", text: "text-blue-800", bar: "bg-blue-500" },
  reviews: {
    label: "Review sites",
    bg: "bg-amber-100",
    text: "text-amber-800",
    bar: "bg-amber-500",
  },
  editorial: {
    label: "Editorial",
    bg: "bg-purple-100",
    text: "text-purple-800",
    bar: "bg-purple-500",
  },
  community_social: {
    label: "Social",
    bg: "bg-pink-100",
    text: "text-pink-800",
    bar: "bg-pink-500",
  },
  other: {
    label: "Other sources",
    bg: "bg-gray-100",
    text: "text-gray-700",
    bar: "bg-gray-400",
  },
};

const getCategoryMeta = (cat: string) =>
  CATEGORY_META[cat] ?? {
    label: humanizeAiReputationValue(cat),
    bg: "bg-gray-100",
    text: "text-gray-700",
    bar: "bg-gray-400",
  };

const INFLUENCE_LEVEL_STYLES: Record<string, string> = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  emerging: "bg-gray-100 text-gray-700",
};

const CONFIDENCE_STYLES: Record<string, string> = {
  high: "bg-green-100 text-green-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-red-100 text-red-800",
};

const PRIORITY_STYLES: Record<string, string> = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-gray-100 text-gray-700",
};

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface InsightSpec {
  icon: ComponentType<{ className?: string }>;
  variant: "info" | "positive" | "warning";
  heading: string;
  body: string;
}

interface DerivedAction {
  priority: "high" | "medium" | "low";
  title: string;
  rationale: string;
  steps: string[];
}

// ─── Derived-data builders ────────────────────────────────────────────────────

/** Derives actionable insight callouts from the source and citation data. */
const buildInsights = (report: AiReputationReport): InsightSpec[] => {
  const insights: InsightSpec[] = [];
  const sorted = [...report.sources].sort((a, b) => b.influence_score - a.influence_score);
  const topSource = sorted[0];

  if (topSource?.category === "ota") {
    insights.push({
      icon: AlertTriangle,
      variant: "warning",
      heading: "An OTA listing is your #1 AI signal",
      body: `${topSource.domain} (score ${topSource.influence_score}) outranks your own website as the primary source AI engines cite. Keeping this listing accurate, detailed, and photo-rich is your highest-leverage action.`,
    });
  }

  const ownSiteProviders = new Set(
    report.sources.filter((s) => s.category === "own_site").flatMap((s) => s.providers ?? []),
  );
  for (const engine of report.methodology.engines_observed) {
    if (!ownSiteProviders.has(engine)) {
      insights.push({
        icon: Lightbulb,
        variant: "info",
        heading: `${humanizeAiReputationValue(engine)} doesn't cite your website`,
        body: `Add JSON-LD Hotel schema markup, a compelling meta description, and a dedicated couples packages page to make your site citable by ${humanizeAiReputationValue(engine)}.`,
      });
    }
  }

  const reviewsPct = report.source_mix.find((m) => m.category === "reviews")?.percentage ?? 0;
  if (reviewsPct < 15) {
    insights.push({
      icon: AlertTriangle,
      variant: "warning",
      heading: `Review sites represent only ${reviewsPct}% of citations`,
      body: "AI engines use review platforms to assess guest suitability. A low review presence weakens your authority signal for romantic and couples travel queries.",
    });
  }

  const socialPct =
    report.source_mix.find((m) => m.category === "community_social")?.percentage ?? 0;
  if (socialPct <= 2) {
    insights.push({
      icon: Lightbulb,
      variant: "info",
      heading: "Social presence is minimal",
      body: "For romantic and couples positioning, social proof is citable content. Consistent social content around couples experiences gives AI engines more signal for your target occasion.",
    });
  }

  return insights;
};

/** Derives prioritised action recommendations when the report contains no pre-generated actions. */
const buildActions = (report: AiReputationReport): DerivedAction[] => {
  const actions: DerivedAction[] = [];
  const sorted = [...report.sources].sort((a, b) => b.influence_score - a.influence_score);
  const topOta = sorted.find((s) => s.category === "ota");

  if (topOta) {
    actions.push({
      priority: "high",
      title: `Optimize your ${topOta.domain} listing`,
      rationale: `${topOta.domain} is the most-cited source across AI engines (influence score ${topOta.influence_score}). What appears on that listing shapes how AI describes your hotel to prospective guests.`,
      steps: [
        "Update the description with specific spa, pool, and romantic amenity copy",
        "Upload high-quality photos of the spa, couples suites, and pool areas",
        "Keep pricing and availability current to avoid AI citing stale information",
        "Respond to all guest reviews, especially those mentioning romantic experiences",
      ],
    });
  }

  const ownSiteProviders = new Set(
    report.sources.filter((s) => s.category === "own_site").flatMap((s) => s.providers ?? []),
  );
  for (const engine of report.methodology.engines_observed) {
    if (!ownSiteProviders.has(engine)) {
      actions.push({
        priority: "high",
        title: `Make your website visible to ${humanizeAiReputationValue(engine)}`,
        rationale: `${humanizeAiReputationValue(engine)} did not cite your website in any observed answer. Structured data and clear page hierarchy help AI engines discover and cite your content.`,
        steps: [
          "Add JSON-LD Hotel schema markup to your homepage",
          "Write a compelling meta description referencing spa, couples, and romantic getaway",
          "Create a dedicated couples packages page with FAQPage schema",
          "Ensure your sitemap is up to date and submitted to search consoles",
        ],
      });
    }
  }

  const reviewsPct = report.source_mix.find((m) => m.category === "reviews")?.percentage ?? 0;
  if (reviewsPct < 15) {
    actions.push({
      priority: "medium",
      title: "Grow your review platform presence",
      rationale: `Review sites account for only ${reviewsPct}% of AI citations. AI engines use review content to assess guest suitability for specific occasions like romantic weekends.`,
      steps: [
        "Set up a post-stay email asking guests to leave a review",
        "Respond to every review on TripAdvisor and Google within 48 hours",
        "Encourage guests to mention specific romantic or spa experiences",
        "Add a QR code at checkout linking directly to your TripAdvisor review page",
      ],
    });
  }

  const socialPct =
    report.source_mix.find((m) => m.category === "community_social")?.percentage ?? 0;
  if (socialPct <= 2) {
    actions.push({
      priority: "low",
      title: "Build social proof for your romantic positioning",
      rationale:
        "Social content from guests and your own accounts can become citable evidence for AI engines assessing romantic suitability.",
      steps: [
        "Post consistent content about couples experiences — spa days, romantic dinners, anniversary stays",
        "Create a branded hashtag and display it in rooms and at spa reception",
        "Encourage user-generated content by offering a small incentive for guests who tag you",
        "Pin a highlights reel of romantic stays to your Instagram and Facebook profiles",
      ],
    });
  }

  return actions;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const InsightCallout = ({
  icon: Icon,
  children,
  variant = "info",
}: {
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  variant?: "info" | "positive" | "warning";
}) => {
  const styles = {
    info: "bg-blue-50 border-blue-200 text-blue-900",
    positive: "bg-green-50 border-green-200 text-green-900",
    warning: "bg-amber-50 border-amber-200 text-amber-900",
  };
  const iconStyles = {
    info: "text-blue-500",
    positive: "text-green-500",
    warning: "text-amber-500",
  };
  return (
    <div className={`flex gap-3 rounded-xl border p-4 ${styles[variant]}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconStyles[variant]}`} />
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
};

const ExternalLink = ({ url, children }: { url: string; children: ReactNode }) =>
  isSafeOutboundUrl(url) ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="break-all text-primary underline"
    >
      {children}
    </a>
  ) : (
    <span className="break-all">{children}</span>
  );

const CategoryBadge = ({ category }: { category: string }) => {
  const { bg, text, label } = getCategoryMeta(category);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bg} ${text}`}
    >
      {label}
    </span>
  );
};

const InfluenceLevelBadge = ({ level }: { level: string }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${INFLUENCE_LEVEL_STYLES[level] ?? "bg-gray-100 text-gray-700"}`}
  >
    {humanizeAiReputationValue(level)}
  </span>
);

const PriorityBadge = ({ priority }: { priority: string }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PRIORITY_STYLES[priority] ?? "bg-gray-100 text-gray-700"}`}
  >
    {humanizeAiReputationValue(priority)} priority
  </span>
);

const ConfidenceBadge = ({ confidence }: { confidence: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${CONFIDENCE_STYLES[confidence] ?? "bg-gray-100 text-gray-700"}`}
  >
    {humanizeAiReputationValue(confidence)} confidence
  </span>
);

const ClaimCard = ({ claim }: { claim: AiReputationClaim }) => (
  <article className="rounded-lg border border-surface-container-high p-3 space-y-1.5">
    <p className="font-medium">{claim.summary}</p>
    <p className="text-sm text-on-surface-variant">
      {humanizeAiReputationValue(claim.alignment)}:{" "}
      {claim.alignment_rationale || "No rationale available."}
    </p>
    {claim.evidence_quote && (
      <blockquote className="border-l-2 border-primary pl-3 text-sm italic">
        {claim.evidence_quote}
      </blockquote>
    )}
    {claim.evidence_basis === "answer_context" && (
      <p className="text-xs italic text-on-surface-variant">
        Association inferred from answer context
      </p>
    )}
  </article>
);

const SourceCard = ({
  source,
  claims,
}: {
  source: AiReputationSource;
  claims: AiReputationClaim[];
}) => (
  <article className="space-y-3 rounded-xl border border-surface-container-high p-4">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <ExternalLink url={source.url}>
          <span className="font-semibold">{source.title || source.url}</span>
        </ExternalLink>
      </div>
      <InfluenceLevelBadge level={source.influence_level} />
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container-high">
      <div
        className={`h-full rounded-full ${getCategoryMeta(source.category).bar}`}
        style={{ width: `${source.influence_score}%` }}
      />
    </div>
    <p className="text-xs text-on-surface-variant">
      Score {source.influence_score} · {source.citation_observations} citations ·{" "}
      {source.provider_count === 1 ? "1 engine" : `${source.provider_count} engines`}:{" "}
      {source.providers?.join(", ") || "unknown"} · {source.prompt_family_count}{" "}
      {source.prompt_family_count === 1 ? "prompt family" : "prompt families"}
    </p>
    {source.evidence_quality === "answer_context" && (
      <p className="text-xs italic text-on-surface-variant">
        Association inferred from answer context
      </p>
    )}
    {claims.length > 0 && (
      <div className="space-y-2 pt-1">
        {claims.map((claim) => (
          <ClaimCard key={claim.claim_id} claim={claim} />
        ))}
      </div>
    )}
  </article>
);

const ActionCard = ({
  title,
  priority,
  rationale,
  steps,
}: {
  title: string;
  priority: string;
  rationale: string;
  steps: string[];
}) => (
  <article className="space-y-3 rounded-xl border border-surface-container-high p-5">
    <div className="flex items-start justify-between gap-3">
      <h3 className="text-lg font-bold leading-snug">{title}</h3>
      <PriorityBadge priority={priority} />
    </div>
    <p className="text-sm text-on-surface-variant">{rationale}</p>
    <ol className="list-decimal space-y-1.5 pl-5 text-sm">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  </article>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AiReputationReportPage = () => {
  const { report_id: reportId } = useParams();
  const [reportState, setReportState] = useState<{
    reportId: string;
    value: AiReputationReport;
  } | null>(null);
  const [errorState, setErrorState] = useState<{ reportId: string; message: string } | null>(null);
  const [tab, setTab] = useState<Tab>("Overview");

  const report = reportState?.reportId === reportId ? reportState.value : null;
  const error = errorState?.reportId === reportId ? errorState.message : null;

  useEffect(() => {
    if (!reportId) return undefined;
    const controller = new AbortController();
    getAiReputationReport(reportId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setReportState({ reportId, value: response.data });
      })
      .catch((caught: unknown) => {
        if (
          !controller.signal.aborted &&
          (!(caught instanceof Error) || caught.name !== "CanceledError")
        )
          setErrorState({ reportId, message: "This report could not be loaded." });
      });
    return () => controller.abort();
  }, [reportId]);

  const claimsBySource = useMemo(
    () =>
      new Map(
        (report?.sources ?? []).map((source) => [
          source.source_id,
          (report?.claims ?? []).filter((claim) => claim.source_ids?.includes(source.source_id)),
        ]),
      ),
    [report],
  );

  if (error || !reportId)
    return (
      <main className="p-12">
        <p role="alert">{error ?? "This report could not be loaded."}</p>
      </main>
    );

  if (!report)
    return (
      <main className="p-12" aria-live="polite">
        Loading AI reputation report...
      </main>
    );

  const sortedSources = [...report.sources].sort((a, b) => b.influence_score - a.influence_score);
  const sortedSourceMix = [...report.source_mix].sort((a, b) => b.percentage - a.percentage);
  const totalCitations = report.source_mix.reduce((sum, m) => sum + m.citation_observations, 0);
  const groups = groupSourcesByDomain(report.sources);
  const matrix = report.commercial_outcome_matrix;
  const matrixSections = [
    { label: "Reinforces positioning", items: matrix.reinforces_positioning },
    { label: "Mixed signals", items: matrix.mixed_signals },
    { label: "Conversion risks", items: matrix.conversion_risks },
    { label: "Positioning gaps", items: matrix.positioning_gaps },
  ].filter(({ items }) => items.length > 0);
  const insights = buildInsights(report);
  const derivedActions = buildActions(report);
  const usesDerivedActions = report.actions.length === 0;

  const overviewStats = [
    { label: "AI answers analyzed", value: report.methodology.valid_answer_count },
    { label: "Sources tracked", value: report.sources.length },
    { label: "Total citations", value: totalCitations },
    { label: "Engines observed", value: report.methodology.engines_observed.length },
  ];

  let content: ReactNode;

  if (tab === "Overview") {
    content = (
      <section className="space-y-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {overviewStats.map(({ label, value }) => (
            <div key={label} className="rounded-xl bg-surface-container p-4 text-center">
              <p className="text-3xl font-extrabold text-primary">{value}</p>
              <p className="mt-1 text-xs text-on-surface-variant">{label}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="mb-4 font-semibold">Where AI engines get their information</h2>
          <div className="space-y-3">
            {sortedSourceMix.map((item) => {
              const meta = getCategoryMeta(String(item.category));
              return (
                <div key={String(item.category)}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="font-medium">{meta.label}</span>
                    <span className="text-on-surface-variant">
                      {item.percentage}% · {item.unique_sources} sources ·{" "}
                      {item.citation_observations} citations
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container-high">
                    <div
                      className={`h-full rounded-full ${meta.bar}`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {sortedSources.length > 0 && (
          <div>
            <h2 className="mb-4 font-semibold">Top 3 most influential sources</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {sortedSources.slice(0, 3).map((source, index) => (
                <div
                  key={source.source_id}
                  className="space-y-2 rounded-xl border border-surface-container-high p-4"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-on-primary">
                      {index + 1}
                    </span>
                    <span className="truncate font-semibold">{source.domain}</span>
                  </div>
                  <p className="line-clamp-2 text-sm text-on-surface-variant">
                    {source.title || source.domain}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <CategoryBadge category={source.category} />
                    <InfluenceLevelBadge level={source.influence_level} />
                  </div>
                  <p className="text-xs text-on-surface-variant">
                    Score {source.influence_score} · {source.citation_observations} citations
                  </p>
                  <p className="text-xs text-on-surface-variant">
                    {source.providers?.join(", ") || "unknown engines"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {insights.length > 0 && (
          <div>
            <h2 className="mb-4 font-semibold">Key observations</h2>
            <div className="space-y-3">
              {insights.map((insight, i) => (
                <InsightCallout key={i} icon={insight.icon} variant={insight.variant}>
                  <p className="font-semibold">{insight.heading}</p>
                  <p>{insight.body}</p>
                </InsightCallout>
              ))}
            </div>
          </div>
        )}

        {report.methodology.confidence_note && (
          <p className="text-xs text-on-surface-variant">{report.methodology.confidence_note}</p>
        )}
      </section>
    );
  } else if (tab === "Sources") {
    content = (
      <section className="space-y-6">
        <p className="text-sm text-on-surface-variant">
          {report.sources.length} unique sources tracked, sorted by influence score.
        </p>
        {report.sources.length === 0 ? (
          <p className="text-on-surface-variant">
            No visible source evidence was available in this sample.
          </p>
        ) : (
          Array.from(groups, ([domain, sources]) => (
            <div key={domain}>
              <div className="mb-3 flex items-center gap-3">
                <h2 className="font-semibold">{domain}</h2>
                <CategoryBadge category={sources[0]?.category ?? "other"} />
              </div>
              <div className="grid gap-3">
                {sources.map((source) => (
                  <SourceCard
                    key={source.source_id}
                    source={source}
                    claims={claimsBySource.get(source.source_id) ?? []}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    );
  } else if (tab === "What to do") {
    content = (
      <section className="space-y-5">
        {usesDerivedActions && (
          <InsightCallout icon={Lightbulb} variant="info">
            <p className="font-semibold">Actions derived from source patterns</p>
            <p>
              This report contains no pre-generated actions. The recommendations below are derived
              from the source and citation data observed.
            </p>
          </InsightCallout>
        )}
        {usesDerivedActions
          ? derivedActions.map((action, i) => (
              <ActionCard
                key={`derived-${i}`}
                title={action.title}
                priority={action.priority}
                rationale={action.rationale}
                steps={action.steps}
              />
            ))
          : report.actions.map((action) => (
              <ActionCard
                key={action.action_id}
                title={action.title}
                priority={action.priority}
                rationale={action.rationale}
                steps={action.steps}
              />
            ))}
      </section>
    );
  } else {
    const ctx = report.commercial_context;
    const summary = report.alignment_summary;
    content = (
      <section className="space-y-8">
        <div className="rounded-xl border border-surface-container-high p-5">
          <h2 className="mb-4 font-semibold">Commercial context used</h2>
          <dl className="space-y-3 text-sm">
            {ctx.intended_positioning && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">
                  Positioning
                </dt>
                <dd className="mt-0.5">{ctx.intended_positioning}</dd>
              </div>
            )}
            {ctx.priority_guest_personas && ctx.priority_guest_personas.length > 0 && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">
                  Priority guests
                </dt>
                <dd className="mt-0.5">{ctx.priority_guest_personas.join(", ")}</dd>
              </div>
            )}
            {ctx.reasons_to_choose && ctx.reasons_to_choose.length > 0 && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">
                  Reasons to choose
                </dt>
                <dd className="mt-0.5">{ctx.reasons_to_choose.join(", ")}</dd>
              </div>
            )}
            {ctx.target_needs_occasions && ctx.target_needs_occasions.length > 0 && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">
                  Occasions
                </dt>
                <dd className="mt-0.5">{ctx.target_needs_occasions.join(", ")}</dd>
              </div>
            )}
            {ctx.real_competitors && ctx.real_competitors.length > 0 && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">
                  Competitors tracked
                </dt>
                <dd className="mt-0.5">{ctx.real_competitors.map((c) => c.name).join(", ")}</dd>
              </div>
            )}
          </dl>
        </div>

        <div>
          <h2 className="mb-4 font-semibold">Alignment summary</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-green-50 p-4 text-center">
              <p className="text-3xl font-extrabold text-green-700">{summary.aligned_claims}</p>
              <p className="mt-1 text-xs text-green-800">Aligned</p>
            </div>
            <div className="rounded-xl bg-amber-50 p-4 text-center">
              <p className="text-3xl font-extrabold text-amber-700">{summary.partial_claims}</p>
              <p className="mt-1 text-xs text-amber-800">Partial</p>
            </div>
            <div className="rounded-xl bg-red-50 p-4 text-center">
              <p className="text-3xl font-extrabold text-red-700">{summary.misaligned_claims}</p>
              <p className="mt-1 text-xs text-red-800">Misaligned</p>
            </div>
            <div className="rounded-xl bg-surface-container p-4 text-center">
              <p className="text-3xl font-extrabold">{summary.unassessed_claims}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Unassessed</p>
            </div>
          </div>
        </div>

        {report.claims.length === 0 ? (
          <div className="space-y-2 rounded-xl border border-surface-container-high p-6 text-center">
            <p className="font-semibold">No claim-level analysis in this report</p>
            <p className="text-sm text-on-surface-variant">
              This report uses source observation only. Claim-level alignment becomes available when
              AI answers can be matched to specific positioning claims.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {report.claims.map((claim) => (
              <ClaimCard key={claim.claim_id} claim={claim} />
            ))}
          </div>
        )}

        {matrixSections.length > 0 && (
          <div>
            <h2 className="mb-4 font-semibold">Commercial outcomes</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {matrixSections.map(({ label, items }) => (
                <div key={label} className="rounded-xl border border-surface-container-high p-4">
                  <h3 className="mb-2 text-sm font-semibold">{label}</h3>
                  <ul className="space-y-2">
                    {items.map((item, idx) => (
                      <li key={`${label}-${idx}`} className="text-sm">
                        {item.title ?? item.summary ?? item.value ?? "Observed item"}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-primary">
            AI Reputation Analysis
          </p>
          <h1 className="mt-1 font-headline text-4xl font-extrabold">
            {report.name || report.business_profile.business_name || "AI reputation analysis"}
          </h1>
          {report.business_profile.website_url && (
            <div className="mt-2 text-sm">
              <ExternalLink url={report.business_profile.website_url}>
                {report.business_profile.website_url}
              </ExternalLink>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConfidenceBadge confidence={report.methodology.confidence} />
          {report.methodology.engines_observed.map((engine) => (
            <span key={engine} className="rounded-full bg-surface-container px-3 py-1 text-sm">
              {humanizeAiReputationValue(engine)}
            </span>
          ))}
        </div>
      </div>

      {report.status === "completed_degraded" && (
        <div className="mt-4">
          <InsightCallout icon={AlertTriangle} variant="warning">
            <p>This report has partial provider results and may not reflect all engines.</p>
          </InsightCallout>
        </div>
      )}

      <nav
        className="mt-8 flex border-b border-surface-container-high"
        role="tablist"
        aria-label="Report sections"
      >
        {TABS.map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            aria-selected={tab === item}
            role="tab"
            className={`-mb-px whitespace-nowrap px-4 py-3 text-sm transition-colors ${
              tab === item
                ? "border-b-2 border-primary font-bold"
                : "text-on-surface-variant hover:bg-surface-container"
            }`}
          >
            {item}
          </button>
        ))}
      </nav>

      <div className="mt-6">{content}</div>
    </main>
  );
};
